import React, { useEffect, useRef, useState } from 'react';
import QuoteForm from 'components/common/modals/ChoiceModal/QuoteForm';
import { useRootStore } from 'store/rootStore';
import { Button, Modal, Toast } from '@insurancemenu/imenu-components-lib';
import { toJS } from 'mobx';
import { HeaderSponsorContentMap } from '@insurancemenu/imenu-components-lib/dist/components/HeaderCommon/sharedStaticData';
import { observer } from 'mobx-react-lite';
import { ChoiceModalType } from 'store/domain/domainStore';
import FormsSpinner from 'components/common/general/FormSpinner';
import { ToastInfo } from 'types/general';
import ContactMeForm from './ContactMeForm';
import { createStyles } from './styles';
import EnrollForm from './EnrollForm';
import NotificationModal from './NotificationModal';

type FormContentMapType = {
  [key in ChoiceModalType]: {
    title: string;
    form: JSX.Element;
    confirmationComponent: JSX.Element;
  };
};

const ChoiceModal = observer((): JSX.Element => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Mobx store
  const {
    domainStore: { domainHeader, choiceModalType, showChoiceModal, cartId, closeChoiceModal },
    contactMeModalStore: { loading: contactMeLoading, collectCartData },
    quoteModalStore: { loading: quoteLoading, toastInfo: quoteToastInfo, closeToast: closeQuoteToast },
    enrollModalStore: { loading: enrollLoading },
  } = useRootStore();

  const { headerStyleId, name: domainName, properties } = toJS(domainHeader);
  const loading = contactMeLoading || quoteLoading || enrollLoading;

  const [modalView, setModalView] = useState<ChoiceModalType>('CONTACT_ME');

  useEffect(() => {
    if (((showChoiceModal && choiceModalType === 'CONTACT_ME') || choiceModalType === 'ALL') && cartId) {
      void collectCartData(cartId);
    }
  }, [showChoiceModal]);

  // Toggle buttons for switching between forms
  const availableModalViews: ChoiceModalType[] = properties?.eStoreEnrollAvailable
    ? ['CONTACT_ME', 'SAVE_CART', 'ENROLL_NOW']
    : ['CONTACT_ME', 'SAVE_CART'];

  // Get sponsor info for displaying logo
  const currentSponsorContent =
    headerStyleId && HeaderSponsorContentMap[headerStyleId]
      ? HeaderSponsorContentMap[headerStyleId]
      : HeaderSponsorContentMap.default;

  const classes = createStyles({
    customLogoWidth: currentSponsorContent.customLogoWidth,
  });

  const onClose = (): void => {
    closeChoiceModal();
    scrollRef.current?.scrollTo(0, 0);
  };

  const ModalToast = (toastInfo: ToastInfo, onToastClose: () => void): JSX.Element => (
    <Toast show={toastInfo.showToast} onClose={onToastClose} colorType={toastInfo.toastType}>
      {toastInfo.toastMessage}
    </Toast>
  );

  const FormContentMap: FormContentMapType = Object.freeze({
    CONTACT_ME: {
      title: 'Contact Me',
      form: <ContactMeForm showFullTitle={choiceModalType !== 'ALL'} />,
      confirmationComponent: <NotificationModal />,
    },
    ENROLL_NOW: {
      title: 'Enroll now',
      form: <EnrollForm showFullTitle={choiceModalType !== 'ALL'} />,
      confirmationComponent: <NotificationModal />,
    },
    SAVE_CART: {
      title: 'Save cart & Email it',
      form: <QuoteForm showFullTitle={choiceModalType !== 'ALL'} />,
      confirmationComponent: ModalToast(quoteToastInfo, closeQuoteToast),
    },
  });

  const ModalViewSwitcher = (
    <div className={classes.buttonsBar}>
      {availableModalViews.map((modalType): JSX.Element => {
        return (
          <Button
            key={modalType}
            type="opaque"
            color={modalType === modalView ? 'primary' : 'default'}
            size="large"
            className={modalType === modalView ? classes.activeButton : classes.inactiveButton}
            onClick={(): void => {
              setModalView(modalType);
            }}
          >
            {FormContentMap[modalType].title}
          </Button>
        );
      })}
    </div>
  );

  return (
    <>
      <Modal show={showChoiceModal} onClose={onClose} className={classes.modal} modalContainerRef={scrollRef}>
        {loading ? (
          <FormsSpinner display={loading} />
        ) : (
          <>
            <div className={classes.logoWrapper}>
              <img
                src={currentSponsorContent.logoUrl}
                alt={`${domainName || 'InsuranceMenu'} logo`}
                className={classes.logo}
              />
            </div>

            {choiceModalType === 'ALL' ? (
              <div>
                <h3 className={classes.title}>What action you would you like to proceed with?</h3>
                {ModalViewSwitcher}
                <div>{FormContentMap[modalView].form}</div>
              </div>
            ) : (
              <>{choiceModalType && <div>{FormContentMap[choiceModalType].form}</div>}</>
            )}
          </>
        )}
      </Modal>
      {choiceModalType &&
        (choiceModalType === 'ALL'
          ? FormContentMap[modalView].confirmationComponent
          : FormContentMap[choiceModalType].confirmationComponent)}
    </>
  );
});

export default ChoiceModal;
